/**
 * Collection sync — the bridge between "the tab saves the whole list on
 * every change" (Phase-1 habit, kept) and a per-entity REST API.
 *
 * `diffCollection(prev, next)` says what changed by id; `syncCollection`
 * turns that into create / update / remove calls, **sequentially**, and
 * returns the id remap for created rows (client ids → server ids) so the
 * tab can adopt the server ids without a reload.
 *
 * Pure module (no React, no fetch) so it is unit-tested with `node --test`.
 */

export function diffCollection(prev, next, { idOf = (x) => x.id, fingerprint }) {
	const prevById = new Map(prev.map(x => [idOf(x), x]))
	const nextIds = new Set(next.map(idOf))
	const created = [], updated = [], removed = []
	for (const item of next) {
		const before = prevById.get(idOf(item))
		if (!before) created.push(item)
		else if (fingerprint(before) !== fingerprint(item)) updated.push(item)
	}
	for (const item of prev) if (!nextIds.has(idOf(item))) removed.push(item)
	return { created, updated, removed }
}

/**
 * Apply a diff through `ops = { create(item) → serverRow, update(id, item), remove(id) }`.
 * Stops at the first failure and reports it with what was already applied,
 * so the caller can reload the server truth — and **which** item failed,
 * `failed: { item, phase }`, so the caller can hold that row back instead
 * of sending it again (CU-869ffwj7a). `phase` is `remove | update |
 * create`; `item` is the row as it was sent. A throw in `create` is not
 * proof of a server refusal — `ops.create` wraps `toServer` before the
 * call and `fromServer` after it — which is why the caller decides by
 * `error.status`, never by the phase.
 */
export async function syncCollection(prev, next, ops, opts) {
	const { created, updated, removed } = diffCollection(prev, next, opts)
	const remap = new Map()
	const applied = { created: 0, updated: 0, removed: 0 }
	// Declared outside the `try`, or the `catch` cannot see what threw.
	let item = null, phase = null
	try {
		phase = 'remove'
		for (item of removed) { await ops.remove(opts.idOf ? opts.idOf(item) : item.id); applied.removed++ }
		phase = 'update'
		for (item of updated) { await ops.update(opts.idOf ? opts.idOf(item) : item.id, item); applied.updated++ }
		phase = 'create'
		for (item of created) {
			const row = await ops.create(item)
			remap.set(opts.idOf ? opts.idOf(item) : item.id, row.id)
			applied.created++
		}
		return { ok: true, remap, applied }
	} catch (error) {
		return { ok: false, error, remap, applied, failed: item ? { item, phase } : null }
	}
}

/**
 * Rewrite client ids to the server ids a sync just handed back.
 *
 * It must be applied to the list as it is **now**, never to the
 * snapshot that was sent. A sync is an await: the operator goes on
 * dragging while it is in flight, and writing the sent snapshot back
 * into state discards everything they did in the meantime. Measured on
 * Category Consistency, 2026-09-28 — three ACRISS reached the server
 * and the client deleted them 200 ms later, because the stale snapshot
 * that came back still carried the old client ids and the next diff
 * read the server's row as one to remove:
 *
 *     13:07:29.306  create  items: ["MBMR","MDMR","EDMR"]
 *     13:07:29.518  delete  that same group
 *     13:07:29.699  create  items: []
 *
 * Rows the remap does not mention are returned untouched, so a row
 * added during the flight survives with its client id and is created
 * by the next cycle.
 */
export function adoptIds(rows, remap) {
	if (!remap || remap.size === 0) return rows
	let changed = false
	const out = rows.map(x => {
		if (!remap.has(x.id)) return x
		changed = true
		return { ...x, id: remap.get(x.id) }
	})
	return changed ? out : rows
}

/** Serial queue: one sync in flight at a time, the latest pending wins. */
export function createSyncQueue(run) {
	let inFlight = null
	let pendingNext = undefined
	async function drain() {
		while (pendingNext !== undefined) {
			const next = pendingNext
			pendingNext = undefined
			await run(next)
		}
		inFlight = null
	}
	return {
		push(next) {
			pendingNext = next
			if (!inFlight) inFlight = drain()
			return inFlight
		},
		get busy() { return inFlight !== null },
	}
}
